import { useAnalyticsContext } from "app/providers/AnalyticsProvider";
import {
  CategoryStandardProduct,
  CreateCartPayload,
  GetOrderResponse,
  Product,
} from "shared/api/middleware-api-client";
import { useFilterIPAddress } from "./useFilterIPAddress";
import { enc, SHA256, MD5 } from "crypto-js";

type AnalyticsFunctionality = {
  onConfig: (product: Product) => Promise<void>;
  onStandardProductConfig: (product: CategoryStandardProduct) => Promise<void>;
  onAddToCart: (product: Product) => void;
  onAddStandardProductToCart: (product: CategoryStandardProduct) => void;
  onViewCart: (cart: CreateCartPayload) => void;
  onViewStandardProductCart: (cart: CreateCartPayload) => void;
  onRemoveFromCart: (product: Product) => void;
  onRemoveStandardProductFromCart: (product: CategoryStandardProduct) => void;
  onBeginCheckout: (cart: CreateCartPayload, voucherName: string) => void;
  onBeginStandardProductCheckout: (
    cart: CreateCartPayload,
    voucherName: string,
  ) => void;
  onAddShippingInfo: (cart: CreateCartPayload, voucherName: string) => void;
  onAddStandardProductShippingInfo: (
    cart: CreateCartPayload,
    voucherName: string,
  ) => void;
  onMeasurementAppointment: (
    order: GetOrderResponse,
    schedulingMoment: string,
  ) => void;
  onAddPaymentInfo: (
    order: GetOrderResponse,
    payment: {
      action: string;
      method: string;
      bank: string;
    },
  ) => void;
  onAddStandardProductPaymentInfo: (
    cart: CreateCartPayload,
    payment: {
      action: string;
      method: string;
      bank: string;
    },
  ) => void;
  onPurchase: (order: GetOrderResponse | undefined) => void;
  onStandardOrderPurchase: (
    order: {
      id: string;
      orderNumber: string;
    },
    cart: CreateCartPayload,
  ) => Promise<void>;
  onAddInstallationAppointment: (order: GetOrderResponse | undefined) => void;
  onSendCartToEmail: (cart: CreateCartPayload | null) => void;
  checkSaleLocation: () => Promise<any>;
};

type ProductCategoryMapping = {
  akoestischepanelen: string;
  accessoire: string;
  gereedschap: string;
  kleurstaalakoestischepanelen: string;
};

const productCategoryMapping: ProductCategoryMapping = {
  akoestischepanelen: "Akoestische Panelen",
  accessoire: "Glasreiniger / Verfset",
  gereedschap: "Accessoires akoestische panelen",
  kleurstaalakoestischepanelen: "Kleurstalen akoestische panelen",
};

const returnCategoryEventName = (category: string): string => {
  return (
    productCategoryMapping[category as keyof ProductCategoryMapping] ||
    "Standard Product Accessoire"
  );
};

const getVisitorType = (isInternalIp: boolean) => {
  return isInternalIp ? "medewerker" : "klant";
};

/**
 * Returns the event name for adding a standard product to the cart based on the product type code.
 * https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#add
 * @param code - The product type code.
 * @returns The event name for adding a standard product to the cart.
 */
const standardProductTypeAddEvent = (code: string) => {
  return "add_to_cart";
};

/**
 * Returns the event name for removing a standard product from the cart based on the product type code.
 * https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#remove
 * @param code - The product type code.
 * @returns The event name for removing a standard product from the cart.
 */
const standardProductTypeRemoveEvent = (code: string) => {
  return "remove_from_cart";
};

export const useAnalytics = (): AnalyticsFunctionality => {
  const { saleLocationResponse, refetchSaleLocation } = useAnalyticsContext();
  const { isCurrentIPInternal } = useFilterIPAddress();

  /**
   * Tracks the addition of a product to the cart.
   *
   * @param product - The product that was added to the cart.
   */
  const onConfig = async (product: Product) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
    const hash = MD5(hashPayload).toString();

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "config_item",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.totalPrice,
        items: [
          {
            item_name: product.doorType.name,
            item_id: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          },
        ],
      },
    });
  };

  /**
   * Tracks the configuration of a standard product.
   *
   * @param product - The standard product that is being configured.
   */
  const onStandardProductConfig = async (product: CategoryStandardProduct) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event = "config_item";

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: event,
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.price * product.amount,
        items: [
          {
            item_name: product.name,
            item_category: returnCategoryEventName(product.productType.code),
            item_id: product.id,
            price: product.price * product.amount,
            item_price: product.price,
            quantity: product.amount,
            item_variant: product.code,
          },
        ],
      },
    });
  };

  /**
   * Tracks the addition of a product to the cart.
   *
   * @param product - The product that was added to the cart.
   */
  const onAddToCart = async (product: Product) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
    const hash = MD5(hashPayload).toString();

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "add_to_cart",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.totalPrice,
        items: [
          {
            item_name: product.doorType.name,
            item_id: hash,
            ean: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          },
        ],
      },
    });
  };

  /**
   * Tracks the view of a product cart.
   *
   * @param cart - The products cart.
   */
  const onViewCart = async (cart: CreateCartPayload) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "view_cart",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: cart.products.map((product) => {
          const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorType.name,
            item_id: hash,
            ean: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          };
        }),
      },
    });
  };

  /**
   * Tracks the view of a standard products cart.
   *
   * @param cart - The standard products products cart.
   */
  const onViewStandardProductCart = async (cart: CreateCartPayload) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "view_cart",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.standardProducts.reduce(
          (sum, product) => sum + product.price * product.amount,
          0,
        ),
        items: cart.standardProducts.map((product) => ({
          item_name: product.name,
          item_category: returnCategoryEventName(product.productType.code),
          item_id: product.id,
          price: product.price * product.amount,
          item_price: product.price,
          quantity: product.amount,
          item_variant: product.code,
        })),
      },
    });
  };

  /**
   * Tracks the addition of a standard product to the cart.
   *
   * @param product - The standard product that was added to the cart.
   */
  const onAddStandardProductToCart = async (
    product: CategoryStandardProduct,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event = standardProductTypeAddEvent(product.productType.code);

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: event,
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.price * product.amount,
        items: [
          {
            item_name: product.name,
            item_category: returnCategoryEventName(product.productType.code),
            item_id: product.id,
            price: product.price * product.amount,
            item_price: product.price,
            quantity: product.amount,
            item_variant: product.code,
          },
        ],
      },
    });
  };

  /**
   * Tracks the removal of a product from the cart.
   *
   * @param product - The product that was removed from the cart.
   */
  const onRemoveFromCart = async (product: Product) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
    const hash = MD5(hashPayload).toString();

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "remove_from_cart",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.totalPrice,
        items: [
          {
            item_name: product.doorType.name,
            item_id: hash,
            ean: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          },
        ],
      },
    });
  };

  /**
   * Tracks the removal of a standard product from the cart.
   *
   * @param product - The standard product that was removed from the cart.
   */
  const onRemoveStandardProductFromCart = async (
    product: CategoryStandardProduct,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event = standardProductTypeRemoveEvent(product.productType.code);

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: event,
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: product.price * product.amount,
        items: [
          {
            item_name: product.name,
            item_category: returnCategoryEventName(product.productType.code),
            item_id: product.id,
            price: product.price * product.amount,
            item_price: product.price,
            quantity: product.amount,
            item_variant: product.code,
          },
        ],
      },
    });
  };

  /**
   * Tracks the beginning of a product checkout.
   *
   * @param cart - The products cart.
   */
  const onBeginCheckout = async (
    cart: CreateCartPayload,
    voucherName: string,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "begin_checkout",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: cart.products.map((product) => {
          const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorType.name,
            item_id: hash,
            ean: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          };
        }),
      },
    };

    if (cart.warrantyPackage) {
      event.ecommerce.gewoonzeker = cart.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = cart.warrantyPackage.price;
    }

    if (cart.discountCode) {
      event.ecommerce.voucher = voucherName;
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the beginning of a standard product checkout.
   *
   * @param cart - The products cart.
   * @param voucherName - The name of the discount voucher.
   */
  const onBeginStandardProductCheckout = async (
    cart: CreateCartPayload,
    voucherName: string,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "begin_checkout",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.standardProducts.reduce(
          (sum, product) => sum + product.price * product.amount,
          0,
        ),
        items: cart.standardProducts.map((product) => ({
          item_name: product.name,
          item_category: returnCategoryEventName(product.productType.code),
          item_id: product.id,
          price: product.price * product.amount,
          item_price: product.price,
          quantity: product.amount,
          item_variant: product.code,
        })),
      },
    };

    if (cart.discountCode) {
      event.ecommerce.voucher = voucherName;
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the adding of shipping info.
   *
   * @param cart - The products cart.
   * @param voucherName - The name of the discount voucher.
   */
  const onAddShippingInfo = async (
    cart: CreateCartPayload,
    voucherName: string,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "add_shipping_info",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: cart.products.map((product) => {
          const hashPayload = `${product.doorType.code}-${product.doorLayout.code}-${product.glassType.code}-${product.doorColor.code}-${product.doorHandle.code}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorType.name,
            item_id: hash,
            ean: hash,
            price: product.doorType.price,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorType.name, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.doorLayout.name,
            item_kleur: product.doorColor.name,
            item_glas: product.glassType.name,
            item_deurbeslag: product.doorHandle.name,
            item_extra_opties: product.extraOptionsNames
              ? product.extraOptionsNames.join(",")
              : "",
            quantity: 1,
            item_price:
              (product.totalPrice || 0) - (product.doorType.price || 0),
          };
        }),
      },
    };

    if (cart.warrantyPackage) {
      event.ecommerce.gewoonzeker = cart.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = cart.warrantyPackage.price;
    }

    if (cart.discountCode) {
      event.ecommerce.voucher = voucherName;
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the adding of a stadard product shipping info.
   *
   * @param cart - The products cart.
   * @param voucherName - The name of the discount voucher.
   */
  const onAddStandardProductShippingInfo = async (
    cart: CreateCartPayload,
    voucherName: string,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "add_shipping_info",
      type_bezoeker: visitorType,
      ecommerce: {
        currencyCode: "EUR",
        value: cart.standardProducts.reduce(
          (sum, product) => sum + product.price * product.amount,
          0,
        ),
        items: cart.standardProducts.map((product) => ({
          item_name: product.name,
          item_category: returnCategoryEventName(product.productType.code),
          item_id: product.id,
          price: product.price * product.amount,
          item_price: product.price,
          quantity: product.amount,
          item_variant: product.code,
        })),
      },
    };

    if (cart.discountCode) {
      event.ecommerce.voucher = voucherName;
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the scheduling of a measurement appointment
   *
   * @param order - The product order.
   * @param schedulingMoment - Scheduling moment value.
   */
  const onMeasurementAppointment = async (
    order: GetOrderResponse,
    schedulingMoment: string,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "inmeetafspraak",
      type_bezoeker: visitorType,
      inplanmoment: schedulingMoment,
      referentienummer: order.orderNumber,
      em: order.invoiceEmail || order.shippingAddress.email,
      em_hashed: SHA256(
        order.invoiceEmail || order.shippingAddress.email,
      ).toString(enc.Hex),
      tel: order.shippingAddress.phone,
      tel_hashed: SHA256(order.shippingAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: order.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: order.products.map((product) => {
          const hashPayload = `${product.doorOption}-${product.layoutOption}-${product.glassOption}-${product.colorOption}-${product.handleOption}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorOption,
            item_id: hash,
            ean: hash,
            price: product.totalPrice,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorOption, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.layoutOption,
            item_kleur: product.colorOption,
            item_glas: product.glassOption,
            item_deurbeslag: product.handleOption,
            item_extra_opties: product.extraOptions
              ? product.extraOptions.map((option) => option.name).join(",")
              : "",
            quantity: 1,
            item_price: product.totalPrice,
          };
        }),
      },
    };

    if (order.warrantyPackage) {
      event.ecommerce.gewoonzeker = order.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = order.warrantyPackage.price;
    }

    // if (order.discountRecords) {
    //   event.ecommerce.voucher = voucherName;
    // }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the scheduling of a measurement appointment
   *
   * @param order - The product order.
   */
  const onAddPaymentInfo = async (
    order: GetOrderResponse,
    payment: { action: string; method: string; bank: string },
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "add_payment_info",
      type_bezoeker: visitorType,
      betaalmethode: payment.action,
      betaalmethode_optie: payment.method,
      betaalmethode_bank: payment.bank,
      referentienummer: order.orderNumber,
      em: order.invoiceEmail || order.shippingAddress.email,
      em_hashed: SHA256(
        order.invoiceEmail || order.shippingAddress.email,
      ).toString(enc.Hex),
      tel: order.shippingAddress.phone,
      tel_hashed: SHA256(order.shippingAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: order.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: order.products.map((product) => {
          const hashPayload = `${product.doorOption}-${product.layoutOption}-${product.glassOption}-${product.colorOption}-${product.handleOption}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorOption,
            item_id: hash,
            ean: hash,
            price: product.totalPrice,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorOption, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.layoutOption,
            item_kleur: product.colorOption,
            item_glas: product.glassOption,
            item_deurbeslag: product.handleOption,
            item_extra_opties: product.extraOptions
              ? product.extraOptions.map((option) => option.name).join(",")
              : "",
            quantity: 1,
            item_price: product.totalPrice,
          };
        }),
      },
    };

    if (order.warrantyPackage) {
      event.ecommerce.gewoonzeker = order.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = order.warrantyPackage.price;
    }

    // if (order.discountRecords) {
    //   event.ecommerce.voucher = voucherName;
    // }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the scheduling of a measurement appointment
   *
   * @param order - The product order.
   */
  const onAddStandardProductPaymentInfo = async (
    cart: CreateCartPayload,
    payment: { action: string; method: string; bank: string },
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "add_payment_info",
      type_bezoeker: visitorType,
      betaalmethode: payment.action,
      betaalmethode_optie: payment.method,
      betaalmethode_bank: payment.bank,
      em: cart.address.emailAddress,
      em_hashed: SHA256(cart.address.emailAddress).toString(enc.Hex),
      tel: cart.address.invoiceAddress.phone,
      tel_hashed: SHA256(cart.address.invoiceAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: cart.standardProducts.reduce(
          (sum, product) => sum + product.price * product.amount,
          0,
        ),
        items: cart.standardProducts.map((product) => ({
          item_name: product.name,
          item_category: returnCategoryEventName(product.productType.code),
          item_id: product.id,
          price: product.price * product.amount,
          item_price: product.price,
          quantity: product.amount,
          item_variant: product.code,
        })),
      },
    };

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the purchase of an order.
   *
   * @param order - The product order.
   */
  const onPurchase = async (order: GetOrderResponse | undefined) => {
    if (saleLocationResponse?.isSaleLocation) return;
    if (!order) return;
    if (!order.paymentData.payments || order.paymentData.payments.length > 1)
      return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "purchase",
      type_bezoeker: visitorType,
      // betaalmethode: payment.action,
      // betaalmethode_optie: payment.method,
      // betaalmethode_bank: payment.bank,
      referentienummer: order.orderNumber,
      transaction_id: order.id,
      em: order.invoiceEmail || order.shippingAddress.email,
      em_hashed: SHA256(
        order.invoiceEmail || order.shippingAddress.email,
      ).toString(enc.Hex),
      tel: order.shippingAddress.phone,
      tel_hashed: SHA256(order.shippingAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: order.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: order.products.map((product) => {
          const hashPayload = `${product.doorOption}-${product.layoutOption}-${product.glassOption}-${product.colorOption}-${product.handleOption}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorOption,
            item_id: hash,
            ean: hash,
            price: product.totalPrice,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorOption, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.layoutOption,
            item_kleur: product.colorOption,
            item_glas: product.glassOption,
            item_deurbeslag: product.handleOption,
            item_extra_opties: product.extraOptions
              ? product.extraOptions.map((option) => option.name).join(",")
              : "",
            quantity: 1,
            item_price: product.totalPrice,
          };
        }),
      },
    };

    if (order.warrantyPackage) {
      event.ecommerce.gewoonzeker = order.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = order.warrantyPackage.price;
    }

    // if (cart.discountCode) {
    //   event.ecommerce.voucher = cart.discountCode;
    // }

    // Send the result to datalayer
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the purchase of a standard order.
   *
   * @param product - The product that was added to the cart.
   */
  const onStandardOrderPurchase = async (
    order: { id: string; orderNumber: string },
    cart: CreateCartPayload,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;
    if (!order) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "purchase",
      type_bezoeker: visitorType,
      // betaalmethode: payment.action,
      // betaalmethode_optie: payment.method,
      // betaalmethode_bank: payment.bank,
      referentienummer: order.orderNumber,
      transaction_id: order.id,
      em: cart.address.emailAddress,
      em_hashed: SHA256(cart.address.emailAddress).toString(enc.Hex),
      tel: cart.address.invoiceAddress.phone,
      tel_hashed: SHA256(cart.address.invoiceAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: cart.standardProducts.reduce(
          (sum, product) => sum + product.price * product.amount,
          0,
        ),
        items: cart.standardProducts.map((product) => ({
          item_name: product.name,
          item_category: returnCategoryEventName(product.productType.code),
          item_id: product.id,
          price: product.price * product.amount,
          item_price: product.price,
          quantity: product.amount,
          item_variant: product.code,
        })),
      },
    };

    // if (order.warrantyPackage) {
    //   event.ecommerce.gewoonzeker = order.warrantyPackage.name;
    //   event.ecommerce.gewoonzeker_kosten = order.warrantyPackage.price;
    // }

    // if (cart.discountCode) {
    //   event.ecommerce.voucher = cart.discountCode;
    // }

    // Send the result to datalayer
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the scheduling of a measurement appointment
   *
   * @param order - The product order.
   */
  const onAddInstallationAppointment = async (
    order: GetOrderResponse | undefined,
  ) => {
    if (saleLocationResponse?.isSaleLocation) return;
    if (!order) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());
    const event: any = {
      event: "montageafspraak",
      type_bezoeker: visitorType,
      // betaalmethode: payment.action,
      // betaalmethode_optie: payment.method,
      // betaalmethode_bank: payment.bank,
      transaction_id: order.id,
      referentienummer: order.orderNumber,
      em: order.invoiceEmail || order.shippingAddress.email,
      em_hashed: SHA256(
        order.invoiceEmail || order.shippingAddress.email,
      ).toString(enc.Hex),
      tel: order.shippingAddress.phone,
      tel_hashed: SHA256(order.shippingAddress.phone).toString(enc.Hex),
      ecommerce: {
        currencyCode: "EUR",
        value: order.products.reduce(
          (sum, product) => sum + (product.totalPrice || 0),
          0,
        ),
        items: order.products.map((product) => {
          const hashPayload = `${product.doorOption}-${product.layoutOption}-${product.glassOption}-${product.colorOption}-${product.handleOption}`;
          const hash = MD5(hashPayload).toString();

          return {
            item_name: product.doorOption,
            item_id: hash,
            ean: hash,
            price: product.totalPrice,
            item_brand: "GewoonGers", // Default value
            item_category: "Deuren", // Category 1, Main category – chosen under 'Products'
            item_category2: product.doorOption, // Category 2, Subcategory – chosen under 'Door type', can be empty if not applicable
            item_ontwerp: product.layoutOption,
            item_kleur: product.colorOption,
            item_glas: product.glassOption,
            item_deurbeslag: product.handleOption,
            item_extra_opties: product.extraOptions
              ? product.extraOptions.map((option) => option.name).join(",")
              : "",
            quantity: 1,
            item_price: product.totalPrice,
          };
        }),
      },
    };

    if (order.warrantyPackage) {
      event.ecommerce.gewoonzeker = order.warrantyPackage.name;
      event.ecommerce.gewoonzeker_kosten = order.warrantyPackage.price;
    }

    // if (order.discountRecords) {
    //   event.ecommerce.voucher = voucherName;
    // }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(event);
  };

  /**
   * Tracks the sending of a standard product to the cart.
   *
   * @param product - The cart that's being sent
   */
  const onSendCartToEmail = async (cart: CreateCartPayload | null) => {
    if (saleLocationResponse?.isSaleLocation) return;

    const visitorType = getVisitorType(await isCurrentIPInternal());

    let products = [];
    for (const product of cart?.products!) {
      products.push({
        name: product.doorType.name,
        category: "Deuren",
        id: product.doorType.code,
        price: product.totalPrice,
        variant: product.doorLayout.name,
      });
    }
    if (!saleLocationResponse?.isSaleLocation) {
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: "shoppingcart-created",
        type_bezoeker: visitorType,
        cart: {
          products: products,
        },
      });
    }
  };

  const checkSaleLocation = () => refetchSaleLocation();

  return {
    onConfig,
    onStandardProductConfig,
    onAddToCart,
    onAddStandardProductToCart,
    onViewCart,
    onViewStandardProductCart,
    onRemoveFromCart,
    onRemoveStandardProductFromCart,
    onBeginCheckout,
    onBeginStandardProductCheckout,
    onAddShippingInfo,
    onAddStandardProductShippingInfo,
    onMeasurementAppointment,
    onAddPaymentInfo,
    onAddStandardProductPaymentInfo,
    onPurchase,
    onStandardOrderPurchase,
    onAddInstallationAppointment,
    onSendCartToEmail,
    checkSaleLocation,
  };
};
